/**
 * Navigation (plan R17, §2.9 flows 3–4 and 7): the manual nav target, route plotting on R9's knowledge-aware planner
 * and route following. The effective target (`core.nav.target`) is the manual one (`state.navTarget`) or, when none is
 * set and the current system is on the route, the relay toward the next system; arriving at the route's last system
 * clears the route. Targets, routes and system entries are rare events (they may allocate); `updateNav` runs per tick
 * and only measures the distance to the cached target position.
 */
import { dangerOf } from '../../galaxy/layout/danger'
import type { SystemLayout } from '../../galaxy/layout/types'
import { navTargetName } from '../../hud/reach/labels'
import { loadReachMapsConfig, planKnownRoute, type ReachMapsConfig } from '../../maps/model'
import type { ReachCore } from '../../reach/runtime/types'
import { navPointOf } from '../../reach/navPoints'
import { siteRadius } from '../../reach/session/siteRadius'
import type { NavTargetRef, ReachCommand, ReachCommandResult, ReachState } from '../../reach/types'
import { Pos64 } from '../../world/components'
import { TARGET_NONE, TARGET_POINT, TARGET_SPHERE, type CruiseTarget } from './kinematics'
import type { TravelRuntime } from './state'

let mapsConfig: ReachMapsConfig | null = null

function mapsCfg(): ReachMapsConfig {
  mapsConfig ??= loadReachMapsConfig()
  return mapsConfig
}

/** True when POI `id` of the layout is visible to the player: not hidden, or discovered. */
function poiKnown(layout: SystemLayout, state: ReachState, id: string): boolean {
  const poi = layout.pois.find((p) => p.id === id)
  if (poi === undefined) return false
  return !poi.hidden || (state.discoveredPois[layout.systemId]?.includes(id) ?? false)
}

/**
 * Writes the target's position and shape into `out` (bodies are their mass-lock shell, fields their sphere, stations
 * their hull: the bounding sphere of radius R from `siteRadius`, scale build SC-5c; the rest points). False, with
 * `out` untouched, when the layout does not hold it or it is an undiscovered hidden POI.
 */
export function resolveNavTarget(layout: SystemLayout, state: ReachState, ref: NavTargetRef, out: CruiseTarget): boolean {
  let p: readonly [number, number, number] | null = null
  let shape = TARGET_POINT
  let radius = 0
  let station = -1
  switch (ref.kind) {
    case 'station': {
      const i = layout.stations.findIndex((s) => s.id === ref.id)
      if (i >= 0) {
        const s = layout.stations[i]
        p = s.position
        shape = TARGET_SPHERE
        radius = siteRadius(s)
        station = i
      }
      break
    }
    case 'relay':
      p = layout.relays.find((r) => r.relayId === ref.id)?.position ?? null
      break
    case 'poi':
      p = poiKnown(layout, state, ref.id) ? (layout.pois.find((x) => x.id === ref.id)?.position ?? null) : null
      break
    case 'laneRing':
      p = layout.laneRings.find((r) => r.id === ref.id)?.position ?? null
      break
    case 'hulk': // M9 CH-14a: a found hulk (not in the layout; `reach/navPoints.ts`)
      p = navPointOf(layout.systemId, ref.id)?.position ?? null
      break
    case 'body': {
      const b = layout.bodies.find((x) => x.id === ref.id)
      if (b !== undefined) {
        p = b.position
        shape = TARGET_SPHERE
        radius = b.shellM
      }
      break
    }
    case 'field': {
      const f = layout.fields.find((x) => x.id === ref.id)
      if (f !== undefined) {
        p = f.center
        shape = TARGET_SPHERE
        radius = f.radiusM
      }
      break
    }
  }
  if (p === null) return false
  out.shape = shape
  out.x = p[0]
  out.y = p[1]
  out.z = p[2]
  out.radius = radius
  out.station = station
  return true
}

/** The relay site (in `layout`) toward the next system of the route, or null (off the route, or at its end). */
function routeRelay(layout: SystemLayout, route: readonly string[]): { index: number; relayId: string } | null {
  const i = route.indexOf(layout.systemId)
  if (i < 0 || i >= route.length - 1) return null
  const next = route[i + 1]
  const relay = layout.relays.find((r) => r.otherSystemId === next)
  return relay === undefined ? null : { index: relay.index, relayId: relay.relayId }
}

/**
 * Recomputes the effective nav target from the manual target, the route and the current layout (rare): clears a route
 * whose last system is the current one, sets `nav.routeNextRelayIndex`, `nav.target` and the cached target position.
 */
export function refreshNav(rt: TravelRuntime, core: ReachCore): void {
  const nav = core.nav
  const state = core.state
  const layout = rt.layout
  let next: { index: number; relayId: string } | null = null
  if (layout !== null && state.route !== null) {
    const i = state.route.indexOf(layout.systemId)
    if (i >= 0 && i === state.route.length - 1) state.route = null
    else next = routeRelay(layout, state.route)
  }
  nav.routeNextRelayIndex = next === null ? -1 : next.index
  let ref: NavTargetRef | null = state.navTarget
  if (ref === null && next !== null) ref = { kind: 'relay', id: next.relayId }
  const t = rt.target
  if (ref !== null && layout !== null && resolveNavTarget(layout, state, ref, t)) {
    nav.target = ref
    nav.x = t.x
    nav.y = t.y
    nav.z = t.z
  } else {
    nav.target = null
    t.shape = TARGET_NONE
    t.station = -1
    nav.x = nav.y = nav.z = 0
    nav.distance = 0
  }
}

/** Per tick: the distance from the player to the nav target (its surface for bodies and fields, its hull for stations). Allocation-free. */
export function updateNav(rt: TravelRuntime, core: ReachCore, eid: number): void {
  const nav = core.nav
  const g = rt.target
  if (g.shape === TARGET_NONE || eid < 0) {
    nav.distance = 0
    return
  }
  const dx = g.x - Pos64.x[eid]
  const dy = g.y - Pos64.y[eid]
  const dz = g.z - Pos64.z[eid]
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  nav.distance = g.shape === TARGET_POINT ? d : d > g.radius ? d - g.radius : 0
}

/**
 * On entering a system: a manual target from the system the ship left through a relay is dropped (it named something
 * over there); otherwise one the new layout does not hold is dropped. Then the effective target is recomputed.
 */
export function enterNav(rt: TravelRuntime, core: ReachCore, viaRelay: boolean): void {
  const state = core.state
  const layout = rt.layout
  if (state.navTarget !== null && (viaRelay || layout === null || !resolveNavTarget(layout, state, state.navTarget, rt.target))) state.navTarget = null
  refreshNav(rt, core)
}

function fail(error: 'unavailable' | 'invalid' | 'busy', message: string): ReachCommandResult {
  return { ok: false, error, message }
}

/** `setNavTarget`: a target in the current layout (a hidden POI only once discovered), or null to clear it. */
export function setNavTargetCommand(rt: TravelRuntime, core: ReachCore, cmd: Extract<ReachCommand, { kind: 'setNavTarget' }>): ReachCommandResult {
  const layout = rt.layout
  if (core.travel.mode === 'transit') return fail('busy', 'Not during a relay jump')
  if (layout === null) return fail('unavailable', 'No system is loaded')
  if (cmd.target === null) {
    core.state.navTarget = null
    refreshNav(rt, core)
    return { ok: true, message: 'Cruise target cleared' }
  }
  const ref: NavTargetRef = { kind: cmd.target.kind, id: cmd.target.id }
  if (!resolveNavTarget(layout, core.state, ref, rt.target)) {
    refreshNav(rt, core)
    return fail('invalid', `No ${ref.kind} ${ref.id} in ${layout.name}`)
  }
  core.state.navTarget = ref
  refreshNav(rt, core)
  return { ok: true, message: `Cruise target: ${navTargetName(layout, core.galaxy, core.state, ref)}` }
}

/**
 * `plotRoute`: R9's knowledge-aware route from the current system (online relays between known systems; "avoid
 * hostile" drops dangerous systems between the ends). A new route replaces the manual target, so the nav target
 * becomes the route's next relay.
 */
export function plotRouteCommand(rt: TravelRuntime, core: ReachCore, cmd: Extract<ReachCommand, { kind: 'plotRoute' }>): ReachCommandResult {
  const layout = rt.layout
  if (core.travel.mode === 'transit') return fail('busy', 'Not during a relay jump')
  if (layout === null) return fail('unavailable', 'No system is loaded')
  const galaxy = core.galaxy
  const dest = galaxy.systems.find((s) => s.id === cmd.toSystemId)
  if (dest === undefined) return fail('invalid', `No system ${cmd.toSystemId}`)
  if (dest.id === layout.systemId) return fail('invalid', `Already in ${dest.name}`)
  const danger = (i: number): number => {
    const s = galaxy.systems[i]
    return s === undefined ? 0 : dangerOf(s)
  }
  const result = planKnownRoute(galaxy, core.state, layout.systemId, dest.id, { avoidHostile: cmd.avoidHostile, danger }, mapsCfg())
  if (result.route === null) return fail('invalid', result.reason === 'unknown' ? `${dest.name} is not charted or rumoured` : `No known route to ${dest.name}`)
  core.state.route = result.route
  core.state.navTarget = null
  refreshNav(rt, core)
  const jumps = result.route.length - 1
  return { ok: true, message: `Route to ${dest.name}: ${jumps} ${jumps === 1 ? 'jump' : 'jumps'}` }
}

/** `clearRoute`: drops the route (the manual target, if any, stays). */
export function clearRouteCommand(rt: TravelRuntime, core: ReachCore): ReachCommandResult {
  const had = core.state.route !== null
  core.state.route = null
  refreshNav(rt, core)
  return { ok: true, message: had ? 'Route cleared' : 'No route to clear' }
}
